/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useLayoutEffect, useRef } from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import { LINE_LENGTH } from './constants';
import { getPageX, setPointerCapture } from './utils';

function usePointerMoveStop(ref, onMove) {
  const lastPageX = useRef(null);
  const pointerTarget = useRef(null);
  useLayoutEffect(() => {
    const node = ref.current;

    function cleanupPointer(evt) {
      lastPageX.current = null;
      const target = pointerTarget.current;
      pointerTarget.current = null;

      // Ignore reason: PointerEvents don't work in JSDOM
      // istanbul ignore if
      if (
        target?.releasePointerCapture &&
        (!target.hasPointerCapture || target.hasPointerCapture(evt.pointerId))
      ) {
        target.releasePointerCapture(evt.pointerId);
      }

      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
    }

    function onPointerMove(evt) {
      const relativeDeltaX = getPageX(evt) - lastPageX.current;
      lastPageX.current = getPageX(evt);
      evt.preventDefault();
      onMove(-relativeDeltaX / LINE_LENGTH);
    }

    function onPointerUp(evt) {
      cleanupPointer(evt);
    }

    function onPointerCancel(evt) {
      cleanupPointer(evt);
    }

    function onPointerDown(evt) {
      if (evt.target === node) {
        return;
      }
      lastPageX.current = getPageX(evt);
      pointerTarget.current = evt.target;
      setPointerCapture(evt);
      evt.preventDefault();
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('pointercancel', onPointerCancel);
    }

    node.addEventListener('pointerdown', onPointerDown);
    return () => {
      node.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
    };
  }, [ref, onMove]);
}

export default usePointerMoveStop;
